import { Dispatch } from "redux";
import { toast } from "react-toastify";
import { finishProcessesingDocument, setDocument, setDocumentFields, setDocumentQuery, setDocuments, setDocumentTemplateValue, setGenerationError, startProcessesingDocument } from "./reducer";
import Employee from "models/employee";
import Doc, { IDocQueryParams } from "models/document";
import * as Yup from "yup";
import capitalize from "utils/capitalize";
import Field from "models/field";
import GeneratedDoc, { IGeneratedDocQueryParams } from "models/generated-doc";
import GenDocStatus from "models/gen-doc-status";
import Recruit from "models/recruit";
import { PaginatedResult } from "models/queryable";
import { buildQueryString } from "utils/query-string";


const ONE_MILLION = 1000000;


export const generateValidationSchema = (fields: Field[]) => {
    const schema: Record<string, any> = {};

    for (const field of fields) {
        const name = field.name;
        const label = field.label ?? name;
        const docField = field.docFields;

        const originalType = field.type; // 'text-area', 'select', 'string', 'number', 'date'
        const normalizedType =
            originalType === "select" || originalType === "text-area"
                ? "string"
                : originalType; // 'string' | 'number' | 'date'

        // Is it required? Default to required
        let isRequired = true;
        if (docField && docField.length > 0)
            isRequired = docField[0].isRequired;


        // base
        schema[name] = Yup[normalizedType as "string" | "number" | "date"]()

        if (isRequired)
            schema[name] = schema[name].required(`${label} is required`);

        if (normalizedType === "number") {
            schema[name] = schema[name]
                .transform((v: unknown, orig: unknown) => {
                    if (typeof orig === "string") {
                        const cleaned = orig.replace(/[\s,￥£€$]/g, "");
                        return cleaned === "" ? NaN : Number(cleaned);
                    }
                    return v;
                })
                .typeError(`${label} must be a number`)
                .min(1, `${label} must be at least 1`)
                .test(
                    "max-2-decimals",
                    `${label} can have at most 2 decimal places`,
                    (value) =>
                        value === undefined ||
                        value === null ||
                        Number.isInteger(value * 100)
                );

            if (name === "salary") {
                schema[name] = schema[name].max(
                    ONE_MILLION - 1,
                    `${label} must be less than ${ONE_MILLION.toLocaleString()}`
                );
            }
        } else if (normalizedType === "string") {
            // Trim so whitespace-only fails min
            schema[name] = schema[name].trim();

            // text-area gets a bigger limit, regular string gets 255
            if (originalType === "text-area") {
                schema[name] = schema[name]
                    .min(1, `${label} too short.`)
                    .max(3000, `${label} too long.`);
            } else {
                schema[name] = schema[name]
                    .min(1, `${label} must be at least 1 character`)
                    .max(255, `${label} must be at most 255 characters`);
            }
        } else if (normalizedType === "date") {
            schema[name] = schema[name].typeError("Must be a valid date");

            if (name === "startDate" || name === "agreementDate") {
                const startOfTomorrow = new Date();
                startOfTomorrow.setHours(0, 0, 0, 0);
                startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
                schema[name] = schema[name].min(
                    startOfTomorrow,
                    `${label} must be after today`
                );
            }
        }
    }

    return Yup.object(schema);
};


// Set individual filter value
export const setDocumentQueryValue = (field: keyof IDocQueryParams, value: string) =>
    (dispatch: Dispatch, getState: any) => {
        const currentFilters = getState().DocumentGenerator.filters;
        const updatedFilters = {
            ...currentFilters,
            [field]: value
        };
        dispatch(setDocumentQuery(updatedFilters));
    };

// Clear all filters
export const clearSearchFilters = () => (dispatch: Dispatch) => {
    const clearedFilters: IDocQueryParams = {
        engineer: '',
        province: '',
        role: '',
        wageType: ''
    };
    dispatch(setDocumentQuery(clearedFilters));
};

export const getDocuments = (query: IDocQueryParams) => async (dispatch: Dispatch) => {
    try {
        const params = new URLSearchParams();
        if (query.engineer) params.append('engineer', query.engineer);
        if (query.province) params.append('province', query.province);
        if (query.role) params.append('role', query.role);
        if (query.wageType) params.append('wageType', query.wageType);
        if (query.employeeType) params.append('employeeType', query.employeeType);

        const url = `${process.env.REACT_APP_API_URL}/documents${params.toString() ? `?${params.toString()}` : ''}`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        dispatch(setDocuments(await res.json()))
        return true;
    } catch (err: any) {
        console.error('Error fetching documents:', err);
        return [];
    }
}

export const getDocument = (documentId: string) => async (dispatch: Dispatch, getState: any) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/${documentId}`;
        const res = await fetch(url, {
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }
        const resData = await res.json();

        const { fields, document } = resData.data;

        dispatch(setDocument(document));
        dispatch(setDocumentFields(fields));
        return document;
    } catch (err: any) {
        console.error('Error fetching documents:', err);
        return null;
    }
}

export const setDocumentValue = (field: any, value: any) =>
    (dispatch: Dispatch, getState: any) => {
        const currentValues = getState().DocumentGenerator.values;
        const updateValues = {
            ...currentValues,
            [field]: value
        };
        dispatch(setDocumentTemplateValue(updateValues));
    };

export const setDocumentValues = (map: any) => (dispatch: Dispatch, getState: any) => {
    const currentValues = getState().HumanResources.values;
    const updateValues = {
        ...currentValues,
        ...map
    };
    dispatch(setDocumentTemplateValue(updateValues));
}

export const saveDocumentAsDraft = (doc: Doc, recruit: Partial<Recruit>, values: any, draftId: string | null) => async (dispatch: Dispatch): Promise<GeneratedDoc | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/save-draft/${doc.id}${draftId ? `/${draftId}` : ''}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ recruit, fields: values }),
            credentials: 'include'
        });

        if (!res.ok) {
            const data = await res.json()
            toast.error(data.message);
            return null;
        }
        const data = await res.json();

        toast.success("Document saved as draft.")
        return data.data.generatedDoc
    } catch (err: any) {
        toast.error("Error drafting document")
        return null;
    }

}

export const submitDocument = (doc: Doc, recruit: Partial<Recruit>, values: any, draftId: string | null) => async (dispatch: Dispatch): Promise<GeneratedDoc | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/submit/${doc.id}${draftId ? `/${draftId}` : ''}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ recruit, fields: values }),
            credentials: 'include'
        });

        if (!res.ok) {
            const data = await res.json()
            toast.error(data.message);
            return null;
        }
        const data = await res.json();
        toast.success("Document submitted.")
        return data.data.generatredDoc
    } catch (err: any) {
        toast.error("Error submitting document")
        return null;
    }


}

export const getGeneratedDocuments = async (query?: IGeneratedDocQueryParams): Promise<PaginatedResult<GeneratedDoc> | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/generated${buildQueryString(query)}`;
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!res.ok) {
            const data = await res.json()
            toast.error(data.message);
            return null;
        }

        const results = await res.json();
        return results.data;
    } catch (err: any) {
        toast.error("Error getting generated docs");
        return null;
    }
}

export const getGeneratedDocsSearchFields = async (): Promise<{
    documents: Doc[];
    statuses: GenDocStatus[];
    managers: Employee[];
} | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/generated/search-fields`;
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!res.ok) {
            const data = await res.json()
            toast.error(data.message);
            return null;
        }

        const results = await res.json();
        return results.data;
    } catch (err: any) {
        toast.error("Error getting search fields");
        return null;
    }
}

export const getGeneratedDocument = async (genDocId: string): Promise<GeneratedDoc | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/documents/generated/${genDocId}`;
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!res.ok) {
            const data = await res.json()
            toast.error(data.message);
            return null;
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error("Error getting generated docs");
        throw new Error('Failed to generate document');
    }
}

export const approveDocument = async (genDocId: string): Promise<GeneratedDoc | null> => {
    const url = `${process.env.REACT_APP_API_URL}/documents/approve/${genDocId}`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include'
    });

    if (!res.ok) {
        const data = await res.json()
        toast.error(data.message);
        return null;
    }

    const data = await res.json();
    return data.data;
}

export const rejectDocument = async (reason: string, genDocId: string): Promise<GeneratedDoc | null> => {
    const url = `${process.env.REACT_APP_API_URL}/documents/reject/${genDocId}`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
    });

    if (!res.ok) {
        const data = await res.json()
        toast.error(data.message);
        return null;
    }

    const data = await res.json();
    return data.data;
}

export const rejectDocumentByCandidate = async (reason: string, genDocId: string): Promise<GeneratedDoc | null> => {
    const url = `${process.env.REACT_APP_API_URL}/documents/reject-by-candidate/${genDocId}`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
    });

    if (!res.ok) {
        const data = await res.json()
        toast.error(data.message);
        return null;
    }

    const data = await res.json();
    return data.data;
}

export const generateDocument = (genDocId: string) => async (dispatch: Dispatch): Promise<boolean> => {

    dispatch(startProcessesingDocument());

    // Generate Document
    try {
        toast.info("Downloading document....")
        const url = `${process.env.REACT_APP_API_URL}/documents/download/${genDocId}`;
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include'
        });

        if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
        }

        toast.success("Document generating, downloading....")
        // Get filename from Content-Disposition header or use default
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = 'generated-document.docx';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Convert response to blob
        const blob = await res.blob();

        // Create download link and trigger download
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        dispatch(finishProcessesingDocument(""))
        return true
    } catch (err: any) {
        return false
    }
}


export const approveOnBehalf = async (genDocId: string, employeeIds: string[]): Promise<GeneratedDoc | null> => {
    const url = `${process.env.REACT_APP_API_URL}/documents/approve-on-behalf/${genDocId}`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: employeeIds }),
    });

    if (!res.ok) {
        const data = await res.json();
        toast.error(data.message);
        return null;
    }

    const data = await res.json();
    return data.data;
}